<template>
  <div id="product-center">
    <!-- 顶部横幅 -->
    <div class="product-center-banner">
      <div class="banner-content">
        <h1>{{ $t('product_center_title') }}</h1>
        <p>{{ $t('product_center_subtitle') }}</p>
      </div>
      <!-- 汉堡菜单按钮 -->
      <div class="hamburger-menu" @click="toggleSidebar">
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
      </div>
    </div>

    <!-- 面包屑 -->
    <div class="breadcrumb">
      <span @click="backToHome" class="breadcrumb-item">{{ $t('breadcrumb_home') }}</span>
      <span class="breadcrumb-separator">></span>
      <span class="breadcrumb-item active">{{ $t('breadcrumb_product_center') }}</span>
    </div>

    <!-- 产品部分 -->
    <section class="products-section">
      <div class="content-wrapper">
        <!-- 侧边栏布局 -->
        <div class="sidebar-layout" :class="{ 'sidebar-overlay-active': isSidebarOpen }" @click="closeSidebarOnOverlay">
          <!-- 侧边栏 -->
          <aside class="sidebar" :class="{ 'sidebar-open': isSidebarOpen }" @click.stop>
            <!-- 分类筛选 -->
            <div class="sidebar-section">
              <h3>{{ $t('product_categories') }}</h3>
              <div class="category-filter">
                <div class="category-item" :class="{ active: selectedCategory === 'all' }" @click="selectCategory('all')">
                  {{ $t('product_all') }}
                </div>
                <div class="category-item" :class="{ active: selectedCategory === 'sopuria' }" @click="selectCategory('sopuria')">
                  {{ $t('product_sopuria') }}
                </div>
                <div class="category-item" :class="{ active: selectedCategory === 'kangertech' }" @click="selectCategory('kangertech')">
                  {{ $t('product_kangertech') }}
                </div>
              </div>
            </div>

            <!-- 特性筛选 -->
            <div class="sidebar-section">
              <h3>{{ $t('product_features') }}</h3>
              <div class="feature-filter">
                <div class="feature-item" :class="{ active: selectedFeatures.includes('pod') }">
                  <input type="checkbox" id="feature-pod" v-model="selectedFeatures" value="pod">
                  <label for="feature-pod">{{ $t('feature_pod') }}</label>
                </div>
                <div class="feature-item" :class="{ active: selectedFeatures.includes('box mod') }">
                  <input type="checkbox" id="feature-box-mod" v-model="selectedFeatures" value="box mod">
                  <label for="feature-box-mod">{{ $t('feature_box_mod_system') }}</label>
                </div>
                <div class="feature-item" :class="{ active: selectedFeatures.includes('disposable') }">
                  <input type="checkbox" id="feature-disposable" v-model="selectedFeatures" value="disposable">
                  <label for="feature-disposable">Disposable</label>
                </div>
                <div class="feature-item" :class="{ active: selectedFeatures.includes('rechargeable') }">
                  <input type="checkbox" id="feature-rechargeable" v-model="selectedFeatures" value="rechargeable">
                  <label for="feature-rechargeable">Rechargeable</label>
                </div>
                <div class="feature-item" :class="{ active: selectedFeatures.includes('adjustable airflow') }">
                  <input type="checkbox" id="feature-adjustable-airflow" v-model="selectedFeatures" value="adjustable airflow">
                  <label for="feature-adjustable-airflow">Airflow Adjustable</label>
                </div>
              </div>
            </div>

            <!-- 排序筛选 -->
            <div class="sidebar-section">
              <h3>{{ $t('product_sort') }}</h3>
              <div class="sort-filter">
                <div class="sort-item" :class="{ active: sortBy === 'name' }" @click="sortBy = 'name'">
                  {{ $t('product_sort_name_az') }}
                </div>
                <div class="sort-item" :class="{ active: sortBy === 'name-desc' }" @click="sortBy = 'name-desc'">
                  {{ $t('product_sort_name_za') }}
                </div>
                <div class="sort-item" :class="{ active: sortBy === 'newest' }" @click="sortBy = 'newest'">
                  {{ $t('product_sort_newest') }}
                </div>
              </div>
            </div>
          </aside>

          <!-- 产品内容区 -->
          <div class="products-content">
            <h2 class="section-title">{{ getContentTitle() }}</h2>
            <p class="content-subtitle">{{ filteredProducts.length }} {{ $t('product_found') }}</p>

            <!-- 产品网格 -->
            <div class="products-grid">
              <div class="product-card" v-for="product in filteredProducts" :key="product.id">
                <div class="product-image">
                  <div class="image-wrapper">
                    <img :src="product.img" :alt="product.name">
                  </div>
                  <div class="category-tag">{{ product.category === 'product_category_sopuria' ? 'Sopuria' : 'Kangertech' }}</div>
                </div>
                <div class="product-info">
                  <h3>{{ product.name }}</h3>
                  <p>{{ $t(product.description) }}</p>
                  <div class="features" v-if="product.features">
                    <span class="feature-tag" v-for="feature in product.features" :key="feature">
                      {{ $t(feature) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { allProducts } from '@/data/products.js'

export default {
  name: 'ProductCenter',
  data() {
    return {
      selectedCategory: 'all',
      selectedFeatures: [],
      sortBy: 'newest',
      isSidebarOpen: false,
      allProducts: allProducts
    };
  },
  computed: {
    filteredProducts() {
      let products = [...this.allProducts];

      // 按分类筛选
      if (this.selectedCategory !== 'all') {
        const categoryKey = this.selectedCategory === 'sopuria' ? 'product_category_sopuria' : 'product_category_kangertech';
        products = products.filter(product => product.category === categoryKey);
      }

      // 按特性筛选
      if (this.selectedFeatures.length > 0) {
        products = products.filter(product => {
          if (!product.features) return false;
          return this.selectedFeatures.some(feature => {
            const featureKey = 'feature_' + feature.replace(/\s+/g, '_');
            return product.features.includes(featureKey);
          });
        });
      }

      // 排序
      products = this.sortProducts(products);

      return products;
    }
  },
  methods: {
    // 返回首页
    backToHome() {
      this.$emit('back-to-home');
      window.scrollTo(0, 0);
    },
    // 选择分类
    selectCategory(category) {
      this.selectedCategory = category;
      // 在移动设备上选择分类后自动关闭侧边栏
      if (window.innerWidth < 1200) {
        this.isSidebarOpen = false;
      }
    },
    // 排序产品
    sortProducts(products) {
      const sorted = [...products];
      
      // 仅在newest排序方式下应用特殊排序规则
      if (this.sortBy === 'newest') {
        // 特殊排序规则：SUBOX MINI(59)排第一，TOGO mini(56)排第二，Top Tank(58)排第三
        const specialOrder = [59, 56, 58];
        
        sorted.sort((a, b) => {
          const aIndex = specialOrder.indexOf(a.id);
          const bIndex = specialOrder.indexOf(b.id);
          
          // 如果两个商品都在特殊排序中，按特殊顺序排列
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          
          // 如果只有一个商品在特殊排序中，该商品排在前面
          if (aIndex !== -1) {
            return -1;
          }
          if (bIndex !== -1) {
            return 1;
          }
          
          // 否则按ID降序排序（最新）
          return b.id - a.id;
        });
      } else {
        // 其他排序方式保持原有逻辑
        switch (this.sortBy) {
          case 'name':
            return sorted.sort((a, b) => a.name.localeCompare(b.name));
          case 'name-desc':
            return sorted.sort((a, b) => b.name.localeCompare(a.name));
          default:
            return sorted;
        }
      }
      
      return sorted;
    },
    // 获取内容标题
    getContentTitle() {
      if (this.selectedCategory === 'all') return this.$t('product_all');
      if (this.selectedCategory === 'sopuria') return this.$t('product_sopuria');
      if (this.selectedCategory === 'kangertech') return this.$t('product_kangertech');
      return this.$t('product_all');
    },
    // 切换侧边栏显示/隐藏
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    // 点击遮罩层关闭侧边栏
    closeSidebarOnOverlay() {
      this.isSidebarOpen = false;
    }
  }
}
</script>
<style lang="scss" scoped>
/* 产品中心页面样式 */
#product-center {
  width: 100%;
  min-height: 100vh;
  background-color: #f5f7fa;
}

/* 面包屑样式 */
.breadcrumb {
  padding: 15px 20px 15px 60px;
  background-color: transparent;
  backdrop-filter: none;
  border-bottom: 1px solid transparent;
  font-size: 16px;
  color: #fff;
  display: flex;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  box-shadow: none;
}

.breadcrumb-item {
  cursor: pointer;
  transition: color 0.3s ease;
}

.breadcrumb-item:hover:not(.active) {
  color: #666666;
}

.breadcrumb-item.active {
  color: #fff;
  font-weight: bold;
}

.breadcrumb-separator {
  margin: 0 10px;
  color: #ccc;
}

/* 顶部横幅 */
.product-center-banner {
  width: 100%;
  height: 400px;
  background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)),
              url('../assets/img/bgtop3.jpg');
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  margin-top: 0;
  padding-top: 0;
  position: relative;
}
  /* 淡入滑入动画 */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .banner-content {
    width: 100%;
    max-width: 100%;
    padding: 0 20px;
    animation: fadeInUp 1s ease-out forwards;
    opacity: 0;
    font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;

    h1 {
      width: 100%;
      font-size: 80px;
      margin-bottom: 30px;
      font-weight: bold;
      text-shadow: 3px 3px 12px rgba(0, 0, 0, 0.6);
      animation: fadeInUp 1s ease-out 0.2s forwards;
      opacity: 0;
      letter-spacing: 3px;
      line-height: 1.2;
    }

    p {
      width: 100%;
      font-size: 48px;
      margin: 0;
      opacity: 0;
      text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.4);
      animation: fadeInUp 1s ease-out 0.4s forwards;
      line-height: 1.4;
      font-weight: 300;
    }
  }

  @media (max-width: 768px) {
    .banner-content {
      h1 {
        font-size: 64px;
        letter-spacing: 2px;
      }

      p {
        font-size: 24px;
      }
    }
  }

  @media (max-width: 480px) {
    .banner-content {
      h1 {
        font-size: 48px;
        letter-spacing: 1px;
      }

      p {
        font-size: 18px;
      }
    }
  }

/* 汉堡菜单样式 */
.hamburger-menu {
  position: fixed;
  top: 1rem;
  right: 1.5rem;
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 25px;
  height: 18px;
  cursor: pointer;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.7);
  padding: 0.6rem;
  border-radius: 6px;
  /* 确保没有其他样式影响 */
  overflow: visible;
  box-sizing: content-box;
}

.hamburger-line {
  width: 100%;
  height: 2px;
  background-color: #ffffff;
  border-radius: 3px;
  transition: all 0.3s ease;
  /* 确保线条不被重叠或隐藏 */
  display: block;
  margin: 0;
  padding: 0;
}

/* 1200px以下响应式调整 */
@media (max-width: 1200px) {
  /* 显示汉堡菜单 */
  .hamburger-menu {
    display: flex;
    top: 0.75rem;
    right: 1rem;
    width: 23px;
    height: 17px;
    padding: 0.5rem;
  }

  /* 调整面包屑缩进 */
  .breadcrumb {
    padding: 15px 20px 15px 20px;
  }

  /* 调整横幅内容 */
  .product-center-banner .banner-content {
    h1 {
      font-size: 96px;
    }

    p {
      font-size: 36px;
    }
  }

  .hamburger-line {
    height: 2.5px;
  }

  /* 调整主内容区 */
  .products-content {
    width: 100%;
  }
}

/* 侧边栏布局 */
.sidebar-layout {
  display: flex;
  padding: 2rem;
  width: 100%;
  gap: 2rem;
  position: relative;

  /* 响应式布局 */
  @media (max-width: 1024px) {
    flex-direction: column;
  }
}

/* 侧边栏样式 */
.sidebar {
  flex: 0 0 280px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 2rem;
  height: fit-content;
  position: sticky;
  top: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  /* 响应式布局 */
  @media (max-width: 1200px) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    z-index: 999;
    background-color: rgba(26, 26, 26, 0.98);
    backdrop-filter: blur(10px);
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
    transform: translateX(-100%);
    overflow-y: auto;
    padding: 2rem;
    box-sizing: border-box;
  }

  @media (max-width: 1024px) {
    position: fixed;
  }

  /* 侧边栏展开状态 */
  &.sidebar-open {
    transform: translateX(0);
  }
}

/* 侧边栏展开时显示遮罩层 */
.sidebar-layout.sidebar-overlay-active::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;
}

  .sidebar-section {
    margin-bottom: 2rem;

    &:last-child {
      margin-bottom: 0;
    }

    h3 {
      font-size: 1.5rem;
      color: #fff;
      margin-bottom: 1rem;
      font-weight: 700;
      letter-spacing: 1px;
      border-bottom: 2px solid #df6f45;
      padding-bottom: 0.5rem;
    }
  }

  /* 分类筛选 */
  .category-filter {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .category-item {
      padding: 0.75rem 1rem;
      background-color: rgba(255, 255, 255, 0.05);
      border-radius: 6px;
      color: rgba(255, 255, 255, 0.8);
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover,
      &.active {
        background-color: rgba(223, 111, 69, 0.2);
        color: #df6f45;
        transform: translateX(5px);
      }
    }
  }

  /* 特性筛选 */
  .feature-filter {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .feature-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      background-color: rgba(255, 255, 255, 0.05);
      border-radius: 6px;
      color: rgba(255, 255, 255, 0.8);
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
        color: #fff;
      }

      &.active {
        background-color: rgba(223, 111, 69, 0.2);
        color: #df6f45;
      }

      input[type="checkbox"] {
        accent-color: #df6f45;
        cursor: pointer;
      }
    }
  }

  /* 排序筛选 */
  .sort-filter {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .sort-item {
      padding: 0.75rem 1rem;
      background-color: rgba(255, 255, 255, 0.05);
      border-radius: 6px;
      color: rgba(255, 255, 255, 0.8);
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover,
      &.active {
        background-color: rgba(223, 111, 69, 0.2);
        color: #df6f45;
      }
    }
  }

/* 产品列表样式 */
.products-content {
  flex: 1;

  .section-title {
    font-size: 36px;
    color: #fff;
    font-weight: 700;
    margin-bottom: 2rem;
    font-weight: 700;
    letter-spacing: 1px;
  }

  /* 产品网格 */
  .products-grid {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .product-card {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1),
                inset 0 1px 0 rgba(255, 255, 255, 0.1),
                0 0 20px rgba(138, 43, 226, 0.2),
                0 0 40px rgba(75, 0, 130, 0.1);
    transition: all 0.4s cubic-bezier(0.23, 1, 0.320, 1);
    display: flex;
    gap: 2rem;
    height: auto;
    min-height: 400px;
    position: relative;
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);

    /* 梦幻光晕效果 */
    &::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.05), transparent);
      transform: rotate(30deg);
      animation: shimmer 6s linear infinite;
      pointer-events: none;
      z-index: 0;
    }

    &:hover {
      transform: translateY(-8px) scale(1.02);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
                  inset 0 1px 0 rgba(255, 255, 255, 0.15),
                  0 0 30px rgba(138, 43, 226, 0.3),
                  0 0 60px rgba(75, 0, 130, 0.2);
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.05) 100%);
      border-color: rgba(255, 255, 255, 0.2);
    }

    .product-image {
      width: 100%;
      max-width: 700px;
      aspect-ratio: 16/9;
      overflow: hidden;
      flex-shrink: 1;
      position: relative;
      border-radius: 12px 0 0 12px;
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.5s cubic-bezier(0.23, 1, 0.320, 1), filter 0.5s ease;
        filter: brightness(1.05) contrast(1.05) saturate(1.05);
      }

      &:hover img {
        transform: scale(1.1);
        filter: brightness(1.15) contrast(1.15) saturate(1.2) hue-rotate(5deg);
      }

      /* 梦幻光线效果 */
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
        animation: lightSweep 4s ease-in-out infinite;
        z-index: 1;
      }

      /* 渐变边框效果 */
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 12px 0 0 12px;
        padding: 1px;
        background: linear-gradient(135deg, rgba(138, 43, 226, 0.5), rgba(75, 0, 130, 0.5), rgba(223, 111, 69, 0.5));
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        pointer-events: none;
      }

      .category-tag {
        position: absolute;
        bottom: 15px;
        left: 15px;
        background: linear-gradient(135deg, rgba(223, 111, 69, 0.9), rgba(138, 43, 226, 0.9));
        color: #fff;
        padding: 0.5rem 1rem;
        border-radius: 25px;
        font-size: 0.85rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        z-index: 2;
        box-shadow: 0 4px 12px rgba(223, 111, 69, 0.4);
        transition: all 0.3s ease;
        border: 1px solid rgba(255, 255, 255, 0.2);
      }

      &:hover .category-tag {
        transform: scale(1.05);
        box-shadow: 0 6px 16px rgba(223, 111, 69, 0.6);
      }
    }

    .product-info {
      padding: 2rem;
      flex: 1;
      display: flex;
      flex-direction: column;
      position: relative;
      z-index: 1;

      h3 {
        font-size: 1.75rem;
        color: #fff;
        margin-bottom: 1rem;
        font-weight: 700;
        letter-spacing: 0.5px;
        text-shadow: 0 2px 8px rgba(138, 43, 226, 0.5);
        transition: all 0.3s ease;
      }

      &:hover h3 {
        color: #f0e6ff;
        text-shadow: 0 4px 16px rgba(138, 43, 226, 0.8);
      }

      p {
        font-size: 0.95rem;
        color: rgba(255, 255, 255, 0.85);
        line-height: 1.7;
        margin-bottom: 1.5rem;
        flex: 1;
        overflow: visible;
        text-overflow: unset;
        white-space: normal;
        transition: all 0.3s ease;
      }

      &:hover p {
        color: rgba(255, 255, 255, 0.95);
      }

      /* 特性标签 */
      .features {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        padding-bottom: 0;
        
        .feature-tag {
          background: linear-gradient(135deg, rgba(138, 43, 226, 0.2), rgba(75, 0, 130, 0.2));
          color: rgba(255, 255, 255, 0.9);
          padding: 0.4rem 0.8rem;
          border-radius: 20px;
          font-size: 0.8rem;
          transition: all 0.3s ease;
          border: 1px solid rgba(138, 43, 226, 0.3);
          box-shadow: 0 2px 8px rgba(138, 43, 226, 0.2);
          text-transform: uppercase;
          letter-spacing: 1px;

          &:hover {
            background: linear-gradient(135deg, rgba(138, 43, 226, 0.4), rgba(75, 0, 130, 0.4));
            color: #fff;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(138, 43, 226, 0.4);
          }
        }
      }
    }
  }
}

/* 产品部分通用样式 */
.products-section {
  padding: 8rem 0;
  margin-top: 0;
  background-color: #181818;
  background-repeat: no-repeat;
  /* 增强的线条纹理，与主页保持一致 */
  background-image:
    linear-gradient(to right, rgba(255,255,255,0.12) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255,255,255,0.12) 1px, transparent 1px);
  background-size: 25px 25px;
  position: relative;
  overflow: hidden;
  /* 线条移动动画 */
  animation: linesMove 15s linear infinite;

  .container {
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    padding: 0;
  }

  /* 产品内容区域的内边距 */
  .content-wrapper {
    padding: 0 20px;
    position: relative;
    z-index: 2;
  }
}

/* 产品部分边缘模糊效果 */
.products-section::before,
.products-section::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 100px;
  z-index: 1;
}

.products-section::before {
  top: 0;
  background: linear-gradient(to bottom, rgba(24, 24, 24, 0.9), transparent);
  backdrop-filter: blur(10px);
}

.products-section::after {
  bottom: 0;
  background: linear-gradient(to top, rgba(24, 24, 24, 0.9), transparent);
  backdrop-filter: blur(10px);
}

/* 线条移动动画 */
@keyframes linesMove {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 25px 25px;
  }
}

/* 梦幻光晕动画 */
@keyframes shimmer {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(30deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(30deg);
  }
}

/* 光线扫描动画 */
@keyframes lightSweep {
  0% {
    left: -100%;
  }
  50% {
    left: 100%;
  }
  100% {
    left: 100%;
  }
}

/* 响应式适配 */
@media (max-width: 480px) {
  .sidebar-layout {
    padding: 0;
    gap: 1rem;
  }

  .content-wrapper {
    padding: 0;
  }

  .products-section {
    padding: 4rem 0;
  }

  .product-center-banner {
    padding: 2rem 1rem;
  }

  .breadcrumb {
    padding: 10px 10px 10px 10px;
  }
}

@media (max-width: 1200px) {
  .product-card {
    flex-direction: column;
    height: auto;
    min-height: unset;
    gap: 0.4rem;

    .product-image {
      width: 100%;
      max-width: 100%;
      aspect-ratio: 16/9;
      flex-shrink: 1;
    }

    .product-info {
      padding: 0.6rem;
      flex: none;
      width: 100%;
      box-sizing: border-box;

      h3 {
        font-size: 1.05rem;
        margin-bottom: 0.35rem;
        line-height: 1.25;
      }

      p {
        font-size: 0.72rem;
        line-height: 1.25;
        margin-bottom: 0.1rem;
        overflow: visible;
        text-overflow: unset;
        white-space: normal;
      }

      .features {
        gap: 0.25rem;
        margin: 0;

        .feature-tag {
          padding: 0.12rem 0.4rem;
          font-size: 0.58rem;
        }
      }
    }
  }
}

@media (max-width: 992px) {
  .products-content {
    .section-title {
      font-size: 28px;
    }
  }

  .product-card {
    flex-direction: column;
    height: auto;
    min-height: unset;
    gap: 0.4rem;

    .product-image {
      width: 100%;
      aspect-ratio: 16/9;
      flex-shrink: 0;
    }

    .product-info {
      padding: 0.6rem;
      flex: none;
      width: 100%;

      h3 {
        font-size: 1.05rem;
        margin-bottom: 0.35rem;
        line-height: 1.25;
      }

      p {
        font-size: 0.72rem;
        line-height: 1.25;
        margin-bottom: 0.1rem;
        overflow: visible;
        text-overflow: unset;
        white-space: normal;
      }

      .features {
        gap: 0.25rem;
        margin: 0;

        .feature-tag {
          padding: 0.12rem 0.4rem;
          font-size: 0.58rem;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .sidebar-layout {
    flex-direction: column;

    .sidebar {
      width: 100%;
      padding: 0.6rem;
      margin-bottom: 1.25rem;
    }

    .content-wrapper {
      padding: 0 0.6rem;
    }
  }

  .products-content {
    .section-title {
      font-size: 24px;
      margin-bottom: 1.25rem;
    }

    .content-subtitle {
      font-size: 0.9rem;
      margin-bottom: 1rem;
    }

    .products-grid {
      gap: 1.25rem;
    }
  }

  .product-card {
    height: auto;
    min-height: unset;
    gap: 0.1rem;

    .product-image {
      width: 100%;
      aspect-ratio: 16/9;
      flex-shrink: 0;
      margin-bottom: 0;
    }

    .product-info {
      padding: 0.1rem 0.5rem 0.5rem;
      margin-top: 0;

      h3 {
        font-size: 0.95rem;
        margin-bottom: 0.15rem;
        line-height: 1.2;
      }

      p {
        font-size: 0.68rem;
        line-height: 1.2;
        margin-bottom: 0.2rem;
        margin-top: 0;
        overflow: visible;
        text-overflow: unset;
        white-space: normal;
      }

      .features {
        gap: 0.25rem;
        margin: 0.15rem 0;
        padding-top: 0;

        .feature-tag {
          padding: 0.15rem 0.4rem;
          font-size: 0.6rem;
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .products-content {
    .section-title {
      font-size: 20px;
      margin-bottom: 1rem;
    }

    .content-subtitle {
      font-size: 0.8rem;
      margin-bottom: 0.75rem;
    }

    .products-grid {
      gap: 1rem;
    }
  }

  .product-card {
      height: auto !important;
      min-height: unset !important;
      gap: 0.2rem !important; /* 保持适当间距，避免内容重合 */

      .product-image {
        width: 100% !important;
        aspect-ratio: 16/9 !important; /* 保持图片大小不变 */
        flex-shrink: 0 !important;
        margin-bottom: 0 !important;
      }

      .product-info {
        padding: 0.2rem 0.5rem 1rem !important;
        margin-top: 0 !important;
      }

      .product-info h3 {
        font-size: 0.75rem;
        margin-bottom: 0.1rem;
        line-height: 1.2;
        display: block;
        overflow: visible;
      }

      .product-info p {
        font-size: 0.55rem;
        line-height: 1.2;
        margin-bottom: 0.2rem;
        margin-top: 0;
        display: block;
        overflow: visible;
        text-overflow: unset;
        white-space: normal;
      }

      .product-info .features {
        gap: 0.15rem;
        margin: 0.15rem 0;
        display: flex;
        flex-wrap: wrap;
        overflow: visible;
        padding-top: 0;
        padding-bottom: 0.1rem;

        /* 隐藏滚动条但保持功能 */
        &::-webkit-scrollbar {
          height: 0;
        }

        .feature-tag {
          padding: 0.1rem 0.3rem;
          font-size: 0.45rem;
          line-height: 1.4;
          white-space: nowrap;
        }
      }
  }
}
</style>
